import {
  type BannerI18nStrings,
  type ConsentCategory,
  type ConsentChoice,
  type SiteConfig,
  ALL_DENIED,
  ALL_GRANTED,
  choiceFromCategories,
} from "@cmp/shared";

import { el, ensureRoot, injectStylesOnce } from "./dom.js";

export interface BannerCallbacks {
  onAcceptAll: () => void;
  onDeclineAll: () => void;
  onOpenPreferences: () => void;
}

export interface PreferencesCallbacks {
  onAcceptAll: () => void;
  onDeclineAll: () => void;
  onConfirmChoices: (choice: ConsentChoice) => void;
}

interface BannerOpts {
  config: SiteConfig;
  i18n: BannerI18nStrings;
  callbacks: BannerCallbacks;
  stylesCss: string;
}

interface PreferencesOpts {
  config: SiteConfig;
  i18n: BannerI18nStrings;
  callbacks: PreferencesCallbacks;
  stylesCss: string;
  initial: ConsentChoice;
}

function applyAccent(root: HTMLElement, accent: string): void {
  root.style.setProperty("--cmp-accent", accent);
}

/**
 * Render `{placeholder}` inside a template into DOM nodes. Unknown
 * placeholders are rendered as plain text.
 */
function renderTemplate(
  template: string,
  replacements: Record<string, Node>,
): Node[] {
  const parts: Node[] = [];
  const re = /\{(\w+)\}/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(template)) !== null) {
    if (match.index > lastIndex) {
      parts.push(document.createTextNode(template.slice(lastIndex, match.index)));
    }
    const key = match[1]!;
    const node = replacements[key];
    if (node) parts.push(node.cloneNode(true));
    else parts.push(document.createTextNode(match[0]));
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < template.length) {
    parts.push(document.createTextNode(template.slice(lastIndex)));
  }
  return parts;
}

// ---------------------------------------------------------------------------
// Banner (bottom bar)
// ---------------------------------------------------------------------------

export function renderBanner(opts: BannerOpts): () => void {
  injectStylesOnce(opts.stylesCss);
  const root = ensureRoot();
  applyAccent(root, opts.config.accent_color);

  // Hide the pill if it exists — banner takes precedence.
  root.querySelector("[data-cmp-pill]")?.remove();

  const { i18n, config, callbacks } = opts;

  const cookiePolicyLink = el(
    "a",
    { href: config.privacy_policy_url, target: "_blank", rel: "noopener" },
    [i18n.cookie_policy_link],
  );

  const preferencesLink = el(
    "a",
    { href: "#", "data-cmp-action": "customize", role: "button" },
    [i18n.preferences_link],
  );

  const body = el(
    "p",
    { id: "cmp-banner-body" },
    renderTemplate(i18n.body, {
      cookie_policy: cookiePolicyLink,
      preferences: preferencesLink,
    }),
  );

  const dim = el("div", { class: "cmp-banner-dim", "data-cmp-banner-dim": "" });

  const bar = el(
    "div",
    {
      class: "cmp-banner-bar",
      "data-cmp-banner-bar": "",
      role: "dialog",
      "aria-modal": "false",
      "aria-labelledby": "cmp-banner-title",
      "aria-describedby": "cmp-banner-body",
    },
    [
      el("div", { class: "cmp-banner-inner" }, [
        el("div", { class: "cmp-banner-copy" }, [
          el("h2", { id: "cmp-banner-title" }, [i18n.title]),
          body,
        ]),
        el("div", { class: "cmp-banner-actions" }, [
          el(
            "button",
            {
              type: "button",
              class: "cmp-btn cmp-btn-outline-accent",
              "data-cmp-action": "decline_all",
            },
            [i18n.decline_all],
          ),
          el(
            "button",
            {
              type: "button",
              class: "cmp-btn cmp-btn-primary",
              "data-cmp-action": "accept_all",
            },
            [i18n.accept_all],
          ),
        ]),
      ]),
    ],
  );

  const close = (): void => {
    bar.remove();
    dim.remove();
  };

  bar.addEventListener("click", (ev) => {
    const target = ev.target as HTMLElement | null;
    const trigger = target?.closest<HTMLElement>("[data-cmp-action]");
    if (!trigger) return;
    const action = trigger.dataset["cmpAction"];
    if (trigger.tagName === "A") ev.preventDefault();
    if (action === "accept_all") {
      close();
      callbacks.onAcceptAll();
    } else if (action === "decline_all") {
      close();
      callbacks.onDeclineAll();
    } else if (action === "customize") {
      // Keep banner + dim mounted; prefs modal opens on top. If the user
      // dismisses prefs without choosing, the banner is still visible.
      callbacks.onOpenPreferences();
    }
  });

  root.replaceChildren(dim, bar);
  bar.querySelector<HTMLButtonElement>('[data-cmp-action="accept_all"]')?.focus();
  return close;
}

// ---------------------------------------------------------------------------
// Preferences modal (tabbed)
// ---------------------------------------------------------------------------

interface TabSpec {
  category: ConsentCategory;
  title: string;
  body: string;
  locked: boolean;
  available: boolean;
}

export function renderPreferences(opts: PreferencesOpts): () => void {
  injectStylesOnce(opts.stylesCss);
  const root = ensureRoot();
  applyAccent(root, opts.config.accent_color);

  const { i18n, config, callbacks } = opts;

  const allTabs: TabSpec[] = [
    {
      category: "necessary",
      title: i18n.category_necessary.title,
      body: i18n.category_necessary.body,
      locked: true,
      available: true,
    },
    {
      category: "performance",
      title: i18n.category_performance.title,
      body: i18n.category_performance.body,
      locked: false,
      available: config.analytics_enabled,
    },
    {
      category: "functional",
      title: i18n.category_functional.title,
      body: i18n.category_functional.body,
      locked: false,
      available: config.functional_enabled,
    },
    {
      category: "targeting",
      title: i18n.category_targeting.title,
      body: i18n.category_targeting.body,
      locked: false,
      available: config.ads_enabled,
    },
  ];
  const tabs: TabSpec[] = allTabs.filter((t) => t.available);

  const state: Record<ConsentCategory, boolean> = {
    necessary: true,
    performance: opts.initial.analytics_storage === "granted" && config.analytics_enabled,
    functional:
      opts.initial.functionality_storage === "granted" && config.functional_enabled,
    targeting: opts.initial.ad_storage === "granted" && config.ads_enabled,
  };

  let activeTab: ConsentCategory = tabs[0]!.category;

  const tabBar = el("div", { class: "cmp-tabs", role: "tablist" });
  const tabBody = el("div", { class: "cmp-tab-body", role: "tabpanel" });

  function renderSwitch(category: ConsentCategory, locked: boolean): HTMLButtonElement {
    const btn = el("button", {
      type: "button",
      class: "cmp-switch",
      role: "switch",
      "aria-checked": state[category] ? "true" : "false",
      "aria-disabled": locked ? "true" : "false",
      "aria-label": `${i18nTitle(category, i18n)} toggle`,
      "data-cmp-category": category,
      tabindex: locked ? -1 : 0,
    }) as HTMLButtonElement;
    if (locked) return btn;
    btn.addEventListener("click", () => {
      state[category] = !state[category];
      btn.setAttribute("aria-checked", state[category] ? "true" : "false");
    });
    return btn;
  }

  function paintTab(category: ConsentCategory): void {
    const spec = tabs.find((t) => t.category === category)!;
    activeTab = category;
    tabBar.querySelectorAll<HTMLButtonElement>("[role=tab]").forEach((el) => {
      const selected = el.dataset["cmpCategory"] === category;
      el.setAttribute("aria-selected", selected ? "true" : "false");
      el.setAttribute("tabindex", selected ? "0" : "-1");
    });

    const toggleRow = spec.locked
      ? el("div", { class: "cmp-tab-toggle" }, [
          el("span", { class: "cmp-tab-toggle-label" }, [i18n.always_active]),
          renderSwitch(spec.category, true),
        ])
      : el("div", { class: "cmp-tab-toggle" }, [
          el("span", { class: "cmp-tab-toggle-label" }, [spec.title]),
          renderSwitch(spec.category, false),
        ]);

    tabBody.replaceChildren(
      el("p", {}, [spec.body]),
      toggleRow,
    );
  }

  for (const t of tabs) {
    const btn = el(
      "button",
      {
        type: "button",
        class: "cmp-tab",
        role: "tab",
        "aria-selected": t.category === activeTab ? "true" : "false",
        tabindex: t.category === activeTab ? "0" : "-1",
        "data-cmp-category": t.category,
      },
      [t.title],
    );
    btn.addEventListener("click", () => paintTab(t.category));
    btn.addEventListener("keydown", (ev) => {
      if (ev.key !== "ArrowLeft" && ev.key !== "ArrowRight") return;
      ev.preventDefault();
      const idx = tabs.findIndex((x) => x.category === activeTab);
      const next = ev.key === "ArrowLeft" ? idx - 1 : idx + 1;
      const target = tabs[(next + tabs.length) % tabs.length]!;
      paintTab(target.category);
      tabBar
        .querySelector<HTMLButtonElement>(`[data-cmp-category="${target.category}"]`)
        ?.focus();
    });
    tabBar.appendChild(btn);
  }
  paintTab(activeTab);

  const closeButton = el("button", {
    type: "button",
    class: "cmp-modal-close",
    "aria-label": "Close",
    "data-cmp-action": "close",
  }, ["×"]);

  const card = el(
    "div",
    {
      class: "cmp-modal-card",
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": "cmp-prefs-title",
      tabindex: "-1",
    },
    [
      el("div", { class: "cmp-modal-header" }, [
        el("h2", { id: "cmp-prefs-title" }, [i18n.modal_title]),
        closeButton,
      ]),
      tabBar,
      tabBody,
      el("div", { class: "cmp-modal-footer" }, [
        el(
          "button",
          {
            type: "button",
            class: "cmp-btn cmp-btn-outline-accent",
            "data-cmp-action": "decline_all",
          },
          [i18n.decline_all],
        ),
        el(
          "button",
          {
            type: "button",
            class: "cmp-btn cmp-btn-outline-accent",
            "data-cmp-action": "confirm_choices",
          },
          [i18n.confirm_choices],
        ),
        el(
          "button",
          {
            type: "button",
            class: "cmp-btn cmp-btn-primary",
            "data-cmp-action": "accept_all",
          },
          [i18n.accept_all],
        ),
      ]),
    ],
  );

  const backdrop = el(
    "div",
    { class: "cmp-backdrop", "data-cmp-backdrop": "prefs" },
    [card],
  );

  const close = (): void => {
    backdrop.remove();
  };

  backdrop.addEventListener("click", (ev) => {
    const target = ev.target as HTMLElement | null;
    if (target?.getAttribute("data-cmp-backdrop") === "prefs") {
      close();
      return;
    }
    const action = target?.closest<HTMLElement>("[data-cmp-action]")?.dataset["cmpAction"];
    if (action === "close") {
      close();
    } else if (action === "accept_all") {
      close();
      callbacks.onAcceptAll();
    } else if (action === "decline_all") {
      close();
      callbacks.onDeclineAll();
    } else if (action === "confirm_choices") {
      const cats: ConsentCategory[] = ["necessary"];
      if (state.performance) cats.push("performance");
      if (state.functional) cats.push("functional");
      if (state.targeting) cats.push("targeting");
      const choice = choiceFromCategories(cats);
      close();
      callbacks.onConfirmChoices(choice);
    }
  });

  const focusablesSelector =
    'button, [tabindex]:not([tabindex="-1"]), a[href], input, select, textarea';
  card.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape") {
      close();
      return;
    }
    if (ev.key !== "Tab") return;
    const focusables = Array.from(
      card.querySelectorAll<HTMLElement>(focusablesSelector),
    ).filter((n) => !n.hasAttribute("aria-disabled") || n.getAttribute("aria-disabled") !== "true");
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    if (ev.shiftKey && document.activeElement === first) {
      ev.preventDefault();
      last.focus();
    } else if (!ev.shiftKey && document.activeElement === last) {
      ev.preventDefault();
      first.focus();
    }
  });

  root.appendChild(backdrop);
  card.focus();
  return close;
}

// ---------------------------------------------------------------------------
// Privacy Choices pill
// ---------------------------------------------------------------------------

export function renderPill(opts: {
  label: string;
  stylesCss: string;
  accent: string;
  onClick: () => void;
}): () => void {
  injectStylesOnce(opts.stylesCss);
  const root = ensureRoot();
  applyAccent(root, opts.accent);
  const existing = root.querySelector("[data-cmp-pill]");
  if (existing) existing.remove();

  const btn = el(
    "button",
    {
      type: "button",
      class: "cmp-pill",
      "data-cmp-pill": "",
      "aria-label": opts.label,
    },
    [opts.label],
  );
  btn.addEventListener("click", opts.onClick);
  root.appendChild(btn);
  return () => btn.remove();
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function buildProofSnapshot(i18n: BannerI18nStrings): string {
  return [
    i18n.title,
    i18n.body,
    `[${i18n.accept_all}] [${i18n.preferences_link}] [${i18n.decline_all}]`,
    `${i18n.category_necessary.title}: ${i18n.category_necessary.body}`,
    `${i18n.category_performance.title}: ${i18n.category_performance.body}`,
    `${i18n.category_functional.title}: ${i18n.category_functional.body}`,
    `${i18n.category_targeting.title}: ${i18n.category_targeting.body}`,
  ].join("\n");
}

export function acceptAllChoice(config: SiteConfig): ConsentChoice {
  const performance = config.analytics_enabled ? "granted" : "denied";
  const functional = config.functional_enabled ? "granted" : "denied";
  const targeting = config.ads_enabled ? "granted" : "denied";
  return {
    security_storage: "granted",
    analytics_storage: performance,
    functionality_storage: functional,
    personalization_storage: functional,
    ad_storage: targeting,
    ad_user_data: targeting,
    ad_personalization: targeting,
  };
}

export function declineAllChoice(): ConsentChoice {
  return { ...ALL_DENIED };
}

export function fullyGrantedChoice(): ConsentChoice {
  return { ...ALL_GRANTED };
}

function i18nTitle(category: ConsentCategory, i18n: BannerI18nStrings): string {
  switch (category) {
    case "necessary":
      return i18n.category_necessary.title;
    case "performance":
      return i18n.category_performance.title;
    case "functional":
      return i18n.category_functional.title;
    case "targeting":
      return i18n.category_targeting.title;
  }
}
